/**
 * `<page-instances>` — the instance list, routed at `/` and `/instances`.
 *
 * It is the product's home because an **instance** is the object a customer
 * comes here to operate: one Sluice deployment, single-tenant by construction,
 * sitting in front of that organization's databases.
 *
 * Implements `SC-F300` (the control-plane record is the desired state and the
 * source of truth — Swarm is a projection, so this list is a database read and
 * keeps working when the cluster does not), `SC-F319` (desired versus observed
 * state, surfaced rather than fused into one optimistic word), `SC-F318` /
 * `SC-F916` (a `deleting` row says that the data is not reclaimed yet, because
 * reporting a completed deletion early would be a compliance failure rather
 * than a cosmetic bug), `SC-F901` (the organization's instance quota and its
 * consumption) and `SC-F901`'s refusal copy, and `SC-S100` / `SC-S907`'s two
 * distinct zero states.
 *
 * Live status arrives over the realtime channel as an **invalidation signal
 * only**: `05` §1.6 makes it a rule that a subscriber refetches through `/v1`
 * rather than reading the record off the socket, so this page never binds a
 * realtime payload into a cell. If the socket cannot be opened the page says so
 * and the manual refresh keeps working — a list that silently stops updating is
 * worse than one that admits it.
 *
 * Stories: `SC-S100`, `SC-S300`, `SC-S317`, `SC-S323`, `SC-S900`, `SC-S907`.
 */

import { LitElement, css, html, nothing, type TemplateResult } from "lit";
import { customElement, query, state } from "lit/decorators.js";

import "../components/index.js";
import type {
  BnBadgeVariant,
  BnColumn,
  BnPageChangeDetail,
  BnRow,
  BnRowClickDetail,
  BnSelectChangeDetail,
  BnSelectOption,
  BnTable,
} from "../components/index.js";

import { ApiError, parseError } from "../api/errors.js";
import {
  listInstances,
  subscribeInstances,
  type Instance,
  type InstanceState,
} from "../api/instances.js";
import { getQuotas, listOrgs, type Organization, type Quota } from "../api/orgs.js";
import { hrefFor, navigate } from "../nav.js";

/**
 * The eleven lifecycle states of `02` §2.1, each with the word the UI uses for
 * it and the tone that word is allowed to carry.
 *
 * Two of them are worth reading twice. `starting` is **not** a failure: `SC-F315`
 * records that `/v1/ready` is fail-closed at boot — datasource health starts
 * false and stays false until the first probe sweep — so a freshly started
 * container answers 503 for several seconds by design. And `healthy` does not
 * promise that queries are being served: the fusion observes task state, health,
 * readiness and the config digest, and none of those observes audit-sink health
 * (`02` §2.1). Neither nuance fits in a badge, so both are stated in a note
 * beside the table when a row is in that state.
 */
const STATES: Record<InstanceState, { label: string; variant: BnBadgeVariant }> = {
  draft: { label: "Draft", variant: "neutral" },
  provisioning: { label: "Provisioning", variant: "info" },
  starting: { label: "Starting", variant: "info" },
  healthy: { label: "Healthy", variant: "success" },
  degraded: { label: "Degraded", variant: "warning" },
  updating: { label: "Updating", variant: "info" },
  "rolling-back": { label: "Rolling back", variant: "warning" },
  suspended: { label: "Suspended", variant: "neutral" },
  deleting: { label: "Deleting", variant: "warning" },
  deleted: { label: "Deleted", variant: "neutral" },
  failed: { label: "Failed", variant: "error" },
};

/** The status filter, in lifecycle order. `""` is "any state". */
const STATUS_OPTIONS: BnSelectOption[] = [
  { value: "", label: "Any state" },
  ...(Object.keys(STATES) as InstanceState[]).map((s) => ({ value: s, label: STATES[s].label })),
];

const COLUMNS: BnColumn[] = [
  { key: "name", label: "Instance" },
  { key: "status", label: "State", width: "1px" },
  { key: "hostname", label: "Hostname", mono: true },
  { key: "size", label: "Size", width: "1px" },
  { key: "version", label: "Sluice", width: "1px", mono: true },
  { key: "generation", label: "Generation", width: "1px", align: "right" },
  { key: "created", label: "Created", width: "1px" },
];

/** How long to coalesce realtime signals before refetching. Several objects
 * change per lifecycle transition and each one wakes us; one refetch per burst
 * is the point of an invalidation signal. */
const REFRESH_DEBOUNCE_MS = 400;

/** Keystroke debounce on the search box, so typing a name is one query. */
const SEARCH_DEBOUNCE_MS = 300;

@customElement("page-instances")
export class PageInstances extends LitElement {
  /** The current page of instances. Never accumulated across pages: keyset
   * pagination hands out one page at a time and `bn-table` holds only cursors. */
  @state() private rows: Instance[] = [];
  /** The keyset cursor for the *next* page, or null on the last one. */
  @state() private next: string | null = null;
  /** The cursor that produced `rows`, replayed on a realtime refetch. */
  @state() private cursor: string | null = null;

  @state() private loading = true;
  @state() private error: ApiError | null = null;

  /** Organizations the caller belongs to. Empty when the roster call failed —
   * which costs the org filter, not the page. */
  @state() private orgs: Organization[] = [];
  /** The instance quota for the organization in scope (`SC-F901`), when there
   * is exactly one organization in scope to ask about. */
  @state() private quota: Quota | null = null;

  @state() private org = "";
  @state() private status: InstanceState | "" = "";
  @state() private q = "";

  /** Whether the realtime invalidation channel is open. False means the list is
   * only as fresh as the last fetch, and the page says so rather than implying
   * live status it is not receiving. */
  @state() private live = false;
  @state() private lastLoaded: Date | null = null;

  @query("bn-table") private tableEl!: BnTable | null;

  private unsubscribe?: () => void;
  private refreshTimer = 0;
  private searchTimer = 0;

  static override styles = css`
    :host {
      display: block;
      font-family: var(--bino-font-sans);
      color: var(--bino-text);
    }
    header {
      display: flex;
      align-items: flex-start;
      flex-wrap: wrap;
      gap: var(--bino-space-md);
      margin-bottom: var(--bino-space-lg);
    }
    h1 {
      margin: 0;
      font-size: var(--bino-font-size-xl);
      font-weight: 600;
    }
    .lede {
      margin: var(--bino-space-xs) 0 0;
      font-size: var(--bino-font-size-md);
      color: var(--bino-text-secondary);
      max-width: 60ch;
    }
    .head-text {
      flex: 1 1 24rem;
      min-width: 0;
    }
    .head-actions {
      display: flex;
      align-items: center;
      gap: var(--bino-space-sm);
    }

    .toolbar {
      display: flex;
      flex-wrap: wrap;
      align-items: flex-end;
      gap: var(--bino-space-md);
      margin-bottom: var(--bino-space-md);
    }
    .toolbar bn-field {
      margin-bottom: 0;
    }
    .search {
      flex: 1 1 var(--bino-search-width);
      max-width: var(--bino-search-width-focus);
    }
    .filter {
      width: 12rem;
    }
    .toolbar-end {
      display: flex;
      align-items: center;
      gap: var(--bino-space-sm);
      margin-left: auto;
    }

    .meta {
      font-size: var(--bino-font-size-sm);
      color: var(--bino-text-secondary);
    }
    .dot {
      display: inline-block;
      width: 0.5rem;
      height: 0.5rem;
      border-radius: var(--bino-radius-pill);
      background: var(--bino-success);
      margin-right: var(--bino-space-xs);
    }
    .dot.off {
      background: var(--bino-neutral);
    }

    .notes {
      display: grid;
      gap: var(--bino-space-sm);
      margin-bottom: var(--bino-space-md);
    }
    .notes p {
      margin: 0;
      font-size: var(--bino-font-size-md);
      color: var(--bino-text-muted);
      max-width: 80ch;
    }

    .cell-name {
      color: var(--bino-primary);
      font-weight: 600;
      text-decoration: none;
    }
    .cell-name:hover {
      text-decoration: underline;
    }
    .cell-name:focus-visible {
      outline: none;
      border-radius: var(--bino-radius-sm);
      box-shadow: var(--bino-focus-ring-shadow);
    }
    .sub {
      display: block;
      font-size: var(--bino-font-size-sm);
      color: var(--bino-text-secondary);
    }

    .error-actions {
      display: flex;
      gap: var(--bino-space-sm);
      margin-top: var(--bino-space-md);
    }
    .error-message {
      margin: 0 0 var(--bino-space-md);
      font-size: var(--bino-font-size-md);
      color: var(--bino-text);
      max-width: 70ch;
    }
    code {
      font-family: var(--bino-font-mono);
      font-size: var(--bino-font-size-base);
    }
  `;

  override connectedCallback() {
    super.connectedCallback();
    // `?org=` is where the chrome's organization switcher lands (`SC-S101`: a
    // switch goes to that organization's instance list), so the filter is read
    // from the URL rather than starting at "all" and contradicting the label in
    // the header. The router matches on pathname only, so the page reads its own
    // query string.
    this.org = new URLSearchParams(location.search).get("org") ?? "";
    void this.loadOrgs();
    void this.load(null);
    void this.listen();
  }

  override disconnectedCallback() {
    super.disconnectedCallback();
    clearTimeout(this.refreshTimer);
    clearTimeout(this.searchTimer);
    this.unsubscribe?.();
    this.unsubscribe = undefined;
    this.live = false;
  }

  /**
   * listen opens the invalidation channel. A failure here is deliberately not a
   * page error: the list is already rendered from `/v1` and stays usable, it
   * just stops updating by itself — which is exactly what the indicator then
   * says.
   */
  private async listen() {
    try {
      this.unsubscribe = await subscribeInstances(() => this.scheduleRefresh());
      this.live = true;
    } catch {
      this.live = false;
    }
  }

  /** scheduleRefresh coalesces a burst of signals into one refetch of the page
   * currently on screen — the cursor, not the first page, so a reader two pages
   * deep is not thrown back to the top by someone else's publish. */
  private scheduleRefresh() {
    clearTimeout(this.refreshTimer);
    this.refreshTimer = window.setTimeout(() => void this.load(this.cursor), REFRESH_DEBOUNCE_MS);
  }

  private async loadOrgs() {
    try {
      const page = await listOrgs();
      this.orgs = page.items;
      void this.loadQuota();
    } catch {
      // The roster is a filter convenience. Losing it costs the org picker and
      // the quota line; the instance list itself is unaffected.
      this.orgs = [];
    }
  }

  /** loadQuota reads the instance quota for the organization in scope. There is
   * one only when the caller filtered to an organization or belongs to exactly
   * one — a consumption number summed across organizations would mean nothing,
   * because the limit is per organization (`SC-F901`). */
  private async loadQuota() {
    const scope = this.org || (this.orgs.length === 1 ? this.orgs[0]!.id : "");
    if (!scope) {
      this.quota = null;
      return;
    }
    try {
      const { items } = await getQuotas(scope);
      this.quota = items.find((qu) => qu.quota === "instances") ?? null;
    } catch {
      this.quota = null;
    }
  }

  private async load(cursor: string | null) {
    this.loading = true;
    try {
      const page = await listInstances({
        org: this.org || undefined,
        status: this.status || undefined,
        q: this.q || undefined,
        cursor: cursor ?? undefined,
      });
      this.rows = page.items;
      this.next = page.next;
      this.cursor = cursor;
      this.error = null;
      this.lastLoaded = new Date();
    } catch (err) {
      this.error = parseError(err);
      this.rows = [];
      this.next = null;
    } finally {
      this.loading = false;
    }
  }

  /** refilter restarts at page one. A keyset cursor is only valid for the query
   * that minted it, so replaying one across a filter change is a 422
   * (`06` §1.5). */
  private refilter() {
    this.tableEl?.resetPagination();
    void this.load(null);
  }

  private onSearch(e: CustomEvent<{ value: string }>) {
    this.q = e.detail.value;
    clearTimeout(this.searchTimer);
    this.searchTimer = window.setTimeout(() => this.refilter(), SEARCH_DEBOUNCE_MS);
  }

  private onStatus(e: CustomEvent<BnSelectChangeDetail>) {
    this.status = e.detail.value as InstanceState | "";
    this.refilter();
  }

  private onOrg(e: CustomEvent<BnSelectChangeDetail>) {
    this.org = e.detail.value;
    this.syncOrgToUrl();
    void this.loadQuota();
    this.refilter();
  }

  private clearFilters() {
    this.q = "";
    this.status = "";
    this.org = "";
    this.syncOrgToUrl();
    void this.loadQuota();
    this.refilter();
  }

  /**
   * syncOrgToUrl writes the organization filter back into `?org=`, so this
   * screen's scope is linkable and so the chrome's switcher — which reads the
   * same parameter — cannot end up naming an organization the rows below are not
   * from. It replaces rather than pushes: a filter is a view of this route, not
   * a route of its own, and it should not cost a back-button press to undo.
   */
  private syncOrgToUrl() {
    const url = new URL(location.href);
    if (this.org) url.searchParams.set("org", this.org);
    else url.searchParams.delete("org");
    navigate(url.pathname + url.search, { replace: true });
  }

  private get filtered(): boolean {
    return this.q !== "" || this.status !== "" || this.org !== "";
  }

  /** The caller may create an instance only where they are an owner
   * (`SC-F102`). With no roster loaded the answer is unknown, and the wizard —
   * which does know — is a better place to say so than a hidden button. */
  private get mayCreate(): boolean {
    return this.orgs.length === 0 || this.orgs.some((o) => o.role === "owner");
  }

  private toRows(): BnRow[] {
    return this.rows.map((i) => ({
      id: i.id,
      name: html`<a class="cell-name" href=${hrefFor("instances", i.id)}
          >${i.display_name || i.name}</a
        >${this.orgLabel(i.org)}`,
      status: this.renderState(i),
      hostname: i.hostname,
      size: i.size,
      version: i.sluice_version,
      generation: i.generation === null ? html`<span class="sub">none</span>` : String(i.generation),
      created: when(i.created),
    }));
  }

  /** The organization is shown under the name only when more than one is in
   * view; on a single-organization account it is noise. */
  private orgLabel(orgId: string): TemplateResult | typeof nothing {
    if (this.org !== "" || this.orgs.length < 2) return nothing;
    const org = this.orgs.find((o) => o.id === orgId);
    return html`<span class="sub">${org ? org.name : orgId}</span>`;
  }

  /**
   * A state cell is the observed state, plus the desired state whenever the two
   * disagree. `SC-F319` exists because fusing them into one word hides exactly
   * the situations an operator is looking for — a suspend that has not taken
   * effect, a delete that is still running.
   */
  private renderState(i: Instance) {
    const meta = STATES[i.observed_state] ?? { label: i.observed_state, variant: "neutral" };
    const drifting =
      (i.desired_state === "suspended" && i.observed_state !== "suspended") ||
      (i.desired_state === "deleted" && i.observed_state !== "deleted") ||
      (i.desired_state === "running" && i.observed_state === "suspended");
    return html`<bn-badge .variant=${meta.variant}>${meta.label}</bn-badge>
      ${drifting ? html`<span class="sub">desired: ${i.desired_state}</span>` : nothing}`;
  }

  /** The notes that keep a state name from being read as more than it is. Each
   * appears only while a row on screen is in the state it explains. */
  private renderNotes() {
    const has = (s: InstanceState) => this.rows.some((i) => i.observed_state === s);
    const notes: Array<TemplateResult | typeof nothing> = [this.renderQuotaWarning()];
    if (has("starting") || has("provisioning")) {
      notes.push(html`<bn-panel eyebrow="Starting is not failed">
        <p>
          An instance answers <code>503</code> on <code>/v1/ready</code> for the first few seconds
          after its container starts. Readiness is fail-closed at boot: datasource health starts
          false and stays false until the first probe sweep completes. That window is labelled
          <strong>Starting</strong>, never Failed.
        </p>
      </bn-panel>`);
    }
    if (has("healthy")) {
      notes.push(html`<bn-panel eyebrow="What Healthy covers">
        <p>
          Healthy fuses task state, <code>/v1/health</code>, <code>/v1/ready</code> and the live
          config digest. It does not observe audit-sink health, so an instance whose state volume is
          full still reads Healthy here while refusing queries. Volume alarms cover that gap; the
          state machine deliberately does not pretend to.
        </p>
      </bn-panel>`);
    }
    if (has("deleting")) {
      notes.push(html`<bn-panel eyebrow="Deleting is not deleted">
        <p>
          A deleting instance keeps its data until the orchestrator confirms that the instance's
          data directory on the claim is gone. Removal of the Pod and its Service proves nothing
          about the disk. The row stays <strong>Deleting</strong> until reclamation is confirmed.
        </p>
      </bn-panel>`);
    }
    const shown = notes.filter((n) => n !== nothing);
    return shown.length === 0 ? nothing : html`<div class="notes">${shown}</div>`;
  }

  /** The consumption line beside the header, when there is one organization in
   * scope to count against (`SC-F901`). */
  private renderQuotaLine() {
    const quota = this.quota;
    if (!quota) return nothing;
    return html`<span class="meta">${quota.current} of ${quota.limit} instances used</span>`;
  }

  /** At the limit, what to do about it. `SC-S900` requires a quota refusal to
   * offer a path rather than a dead end; plans are `v1`, so at MVP the path is
   * the organization's own settings and a human. */
  private renderQuotaWarning() {
    const quota = this.quota;
    if (!quota || quota.current < quota.limit) return nothing;
    const scope = this.org || (this.orgs.length === 1 ? this.orgs[0]!.id : "");
    return html`<bn-panel eyebrow="Instance quota reached">
      <p>
        This organization is using ${quota.current} of ${quota.limit} instances. Creating another is
        refused until the limit is raised. Limits are set per organization by Sluice Cloud
        operations — plans are not self-service yet, so ask your Sluice Cloud contact to raise it.
      </p>
      ${scope
        ? html`<div class="error-actions">
            <bn-button @click=${() => navigate(hrefFor("orgs", scope))}>
              Organization settings
            </bn-button>
          </div>`
        : nothing}
    </bn-panel>`;
  }

  /**
   * The failure state (`SC-S907`): the `apierr` code and message exactly as the
   * envelope carried them, plus the `request_id` a reader quotes in a support
   * ticket. A code the bundle does not know still renders, because `06` §1.3
   * makes a new error code additive rather than breaking.
   */
  private renderError(err: ApiError) {
    return html`<bn-card heading="Could not load instances">
      <div role="alert">
        <p class="error-message">${err.message}</p>
        <bn-kv
          .rows=${[
            { label: "Error code", value: err.code },
            ...(err.requestId ? [{ label: "Request id", value: err.requestId }] : []),
          ]}
        ></bn-kv>
        ${err.requestId
          ? nothing
          : html`<p class="meta">
              The request did not reach Sluice Cloud, so there is no request id to quote. Check your
              connection and try again.
            </p>`}
        <div class="error-actions">
          <bn-button variant="primary" @click=${() => void this.load(this.cursor)}>
            Try again
          </bn-button>
        </div>
      </div>
    </bn-card>`;
  }

  /** The two zero states `SC-S907` requires to be distinguishable: nothing has
   * been created, versus everything has been filtered out. */
  private renderEmpty() {
    if (this.filtered) {
      return html`<bn-empty-state
        icon="⌕"
        heading="No instances match these filters"
        message="This organization may still have instances — the current search, state or organization filter excludes all of them."
      >
        <bn-button slot="actions" @click=${this.clearFilters}>Clear filters</bn-button>
      </bn-empty-state>`;
    }
    return html`<bn-empty-state
      icon="◲"
      heading="No instances yet"
      message="An instance is one Sluice deployment: a single-tenant gateway that sits in front of your databases, decides every query against the policies you publish, and records what it decided. Creating one gives this organization an HTTPS endpoint on its own hostname — no cluster, no YAML."
    >
      ${this.mayCreate
        ? html`<bn-button
            slot="actions"
            variant="primary"
            @click=${() => navigate("/instances/new")}
          >
            Create an instance
          </bn-button>`
        : html`<span slot="actions" class="meta">
            Creating an instance needs the owner role on an organization.
          </span>`}
    </bn-empty-state>`;
  }

  private renderToolbar() {
    const orgOptions: BnSelectOption[] = [
      { value: "", label: "All organizations" },
      ...this.orgs.map((o) => ({ value: o.id, label: o.name })),
    ];
    return html`<div class="toolbar">
      <bn-field class="search" label="Search instances">
        <bn-input
          .value=${this.q}
          type="search"
          placeholder="Name or hostname"
          @input=${this.onSearch}
        ></bn-input>
      </bn-field>
      <bn-field class="filter" label="State">
        <bn-select
          .options=${STATUS_OPTIONS}
          .value=${this.status}
          @change=${this.onStatus}
        ></bn-select>
      </bn-field>
      ${this.orgs.length > 1
        ? html`<bn-field class="filter" label="Organization">
            <bn-select .options=${orgOptions} .value=${this.org} @change=${this.onOrg}></bn-select>
          </bn-field>`
        : nothing}
      <div class="toolbar-end">
        <span class="meta">
          <span class="dot ${this.live ? "" : "off"}" aria-hidden="true"></span>
          ${this.live
            ? "Live"
            : "Live updates unavailable"}${this.lastLoaded
            ? html` · updated ${this.lastLoaded.toLocaleTimeString()}`
            : nothing}
        </span>
        <bn-button size="sm" ?loading=${this.loading} @click=${() => void this.load(this.cursor)}>
          Refresh
        </bn-button>
      </div>
    </div>`;
  }

  override render() {
    return html`
      <header>
        <div class="head-text">
          <h1>Instances</h1>
          <p class="lede">
            Every Sluice deployment this account can reach. State is what the control plane observed,
            not what it asked for — the two are shown separately whenever they differ.
          </p>
        </div>
        <div class="head-actions">
          ${this.renderQuotaLine()}
          ${this.mayCreate
            ? html`<bn-button variant="primary" @click=${() => navigate("/instances/new")}>
                New instance
              </bn-button>`
            : nothing}
        </div>
      </header>

      ${this.renderToolbar()}
      ${this.error
        ? this.renderError(this.error)
        : html`
            ${this.renderNotes()}
            <bn-card flush>
              <bn-table
                caption="Instances"
                clickable
                ?paginated=${this.rows.length > 0}
                row-key="id"
                .columns=${COLUMNS}
                .rows=${this.toRows()}
                .nextCursor=${this.next}
                ?loading=${this.loading}
                @row-click=${(e: CustomEvent<BnRowClickDetail>) =>
                  navigate(hrefFor("instances", String(e.detail.id)))}
                @page-change=${(e: CustomEvent<BnPageChangeDetail>) => void this.load(e.detail.cursor)}
              >
                <div slot="empty">${this.renderEmpty()}</div>
              </bn-table>
            </bn-card>
          `}
    `;
  }
}

/** when renders a timestamp in the reader's locale, and falls back to the raw
 * value rather than to "Invalid Date" if the server ever sends something this
 * bundle cannot parse. */
function when(iso: string): string {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleString();
}

declare global {
  interface HTMLElementTagNameMap {
    "page-instances": PageInstances;
  }
}
